import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { 
  Wrench, Car, Shield, Settings, Droplets, 
  Thermometer, Disc, Battery, ArrowRight, Key, Cpu, Paintbrush, ChevronLeft, ChevronRight, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { getImageUrl } from "@/utils/imageUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Services = () => {
  const [settings, setSettings] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const parseFeatures = (featuresStr) => {
    if (!featuresStr) return [];
    return featuresStr.split('|').filter(f => f.trim());
  };

  const parseImages = (imagesStr) => {
    if (!imagesStr) return [];
    return imagesStr.split(',').filter(s => s.trim());
  };

  const openLightbox = (images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImages([]);
    setLightboxIndex(0);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
  };

  const mainServices = [
    {
      icon: Wrench,
      title: settings?.service1_titre || "Mécanique Générale",
      description: settings?.service1_description || "Entretien complet, vidange, courroie de distribution, embrayage, freins et plus encore.",
      features: parseFeatures(settings?.service1_features || "Diagnostic électronique|Révision complète|Réparation moteur"),
      images: parseImages(settings?.service1_images || "")
    },
    {
      icon: Car,
      title: settings?.service2_titre || "Vente de Véhicules",
      description: settings?.service2_description || "Large sélection de véhicules d'occasion contrôlés et garantis.",
      features: parseFeatures(settings?.service2_features || "Garantie 12 mois|Reprise de votre ancien véhicule|Financement disponible"),
      images: parseImages(settings?.service2_images || "")
    },
    {
      icon: Shield,
      title: settings?.service3_titre || "Contrôle Technique",
      description: settings?.service3_description || "Préparation au contrôle technique et suivi des points de contrôle.",
      features: parseFeatures(settings?.service3_features || "Pré-contrôle gratuit|Réparations sur place|Rendez-vous rapide"),
      images: parseImages(settings?.service3_images || "")
    },
    {
      icon: Key,
      title: settings?.service4_titre || "Serrurerie Automobile",
      description: settings?.service4_description || "Service complet de serrurerie auto : clés perdues, programmation, réparation.",
      features: parseFeatures(settings?.service4_features || "Programmation de clés|Réparation télécommande|Duplication de clés"),
      images: parseImages(settings?.service4_images || "")
    },
    {
      icon: Cpu,
      title: settings?.service5_titre || "Électronique Auto",
      description: settings?.service5_description || "Diagnostic et réparation des systèmes électroniques de votre véhicule.",
      features: parseFeatures(settings?.service5_features || "Diagnostic électronique|Reprogrammation calculateur|Réparation ABS/ESP"),
      images: parseImages(settings?.service5_images || "")
    },
    {
      icon: Paintbrush,
      title: settings?.service6_titre || "Carrosserie",
      description: settings?.service6_description || "Réparation et peinture carrosserie pour redonner à votre véhicule son aspect d'origine.",
      features: parseFeatures(settings?.service6_features || "Débosselage|Peinture|Remplacement pièces"),
      images: parseImages(settings?.service6_images || "")
    }
  ];

  const otherServices = [
    { icon: Settings, title: "Vidange & Filtres", description: "Huile, filtre à huile, filtre à air, filtre habitacle" },
    { icon: Disc, title: "Freinage", description: "Plaquettes, disques, liquide de frein" },
    { icon: Droplets, title: "Climatisation", description: "Recharge, entretien, diagnostic" },
    { icon: Thermometer, title: "Refroidissement", description: "Liquide, radiateur, pompe à eau" },
    { icon: Battery, title: "Électricité Auto", description: "Batterie, alternateur, démarreur" },
  ];

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="relative py-32 bg-gray-50">
        <div className="page-container">
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            {(settings?.services_titre || "NOS SERVICES").split(" ")[0]} <span className="text-yellow-500">{(settings?.services_titre || "NOS SERVICES").split(" ").slice(1).join(" ") || "SERVICES"}</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl">
            {settings?.services_description || "Une gamme complète de services pour l'entretien, la réparation et l'achat de votre véhicule."}
          </p>
        </div>
      </section>

      {/* Main Services */}
      <section className="section-spacing bg-white">
        <div className="page-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <div 
                key={index}
                className="bg-white border border-gray-200 rounded-xl p-8 hover:border-yellow-400 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-16 h-16 rounded-xl bg-yellow-100 flex items-center justify-center mb-6">
                  <service.icon className="w-8 h-8 text-yellow-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-700">
                      <div className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Image Gallery for Service */}
                {service.images.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <p className="text-sm text-gray-500 mb-3">Nos réalisations</p>
                    <div className="grid grid-cols-4 gap-2">
                      {service.images.slice(0, 4).map((img, imgIndex) => (
                        <div 
                          key={imgIndex}
                          className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group"
                          onClick={() => openLightbox(service.images, imgIndex)}
                        >
                          <img 
                            src={getImageUrl(img)} 
                            alt={`${service.title} - ${imgIndex + 1}`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white text-xs font-medium">Voir</span>
                          </div>
                          {imgIndex === 3 && service.images.length > 4 && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                              <span className="text-white font-bold">+{service.images.length - 4}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="section-spacing bg-gray-50">
        <div className="page-container">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            AUTRES <span className="text-yellow-500">PRESTATIONS</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {otherServices.map((service, index) => (
              <div 
                key={index}
                className="bg-white border border-gray-200 rounded-xl p-6 text-center hover:shadow-lg hover:border-yellow-400 transition-all"
              >
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
                  <service.icon className="w-6 h-6 text-yellow-600" />
                </div>
                <h4 className="text-gray-900 font-semibold mb-2">{service.title}</h4>
                <p className="text-gray-500 text-sm">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Note */}
      <section className="section-spacing bg-white">
        <div className="page-container">
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Devis Gratuit
              </h3>
              <p className="text-gray-600">
                Nous établissons un devis détaillé avant toute intervention. 
                Pas de surprise, transparence totale.
              </p>
            </div>
            <Link to="/rendez-vous">
              <Button className="bg-yellow-500 text-white hover:bg-yellow-600 font-bold uppercase whitespace-nowrap">
                Demander un devis
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-yellow-500">
        <div className="page-container text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            {settings?.services_cta_titre || "PRENEZ RENDEZ-VOUS"}
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8 text-lg">
            {settings?.services_cta_description || "Notre équipe de professionnels est à votre disposition pour tous vos besoins automobiles."}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/rendez-vous">
              <Button className="bg-white text-yellow-600 hover:bg-gray-100 font-bold uppercase">
                Prendre rendez-vous
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white/20">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={closeLightbox}>
          <button 
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/20 rounded-full transition-colors"
            onClick={closeLightbox}
          >
            <X className="w-8 h-8" />
          </button>
          
          {lightboxImages.length > 1 && (
            <>
              <button 
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white p-2 hover:bg-white/20 rounded-full transition-colors"
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button 
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white p-2 hover:bg-white/20 rounded-full transition-colors"
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}

          <div className="max-w-4xl max-h-[80vh] p-4" onClick={(e) => e.stopPropagation()}>
            <img 
              src={getImageUrl(lightboxImages[lightboxIndex])} 
              alt={`Image ${lightboxIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <p className="text-white text-center mt-4">
              {lightboxIndex + 1} / {lightboxImages.length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
